import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Pencil, Shield, Trash2 } from "lucide-react";
import { dashboardToast } from '../../utils/dashboardToast';
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import SaListPageShell from "../../components/sa/SaListPageShell";
import RecordDetailModal from "../../components/RecordDetailModal";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { getRoleList, addOrUpdateRole, deleteRole } from "../../services/roleApi";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const Role = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    roleName: "",
    description: "",
    isActive: false,
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const dataFetchedRef = useRef(false);

  const [apiPageInfo, setApiPageInfo] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const fetchRoles = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRoleList(page, pageSize);
      setRoles((result.items || []).map(role => ({
        ...role,
        isActive: role.isActive === 1
      })));
      setApiPageInfo({
        currentPage: result.pageNumber || 1,
        pageSize: result.pageSize || 10,
        totalPages: Math.ceil((result.totalCount || 0) / (result.pageSize || 10)),
        totalCount: result.totalCount || 0,
      });
    } catch (err) {
      setError("Failed to fetch roles. Please try again.");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles(apiPageInfo.currentPage, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  const refreshRoles = useCallback((page = apiPageInfo.currentPage, pageSize = apiPageInfo.pageSize) => {
    fetchRoles(page, pageSize);
  }, [fetchRoles, apiPageInfo.currentPage, apiPageInfo.pageSize]);

  const goToPage = (page) => {
    const newPage = Math.min(Math.max(page, 1), apiPageInfo.totalPages);
    if (newPage !== apiPageInfo.currentPage) {
      fetchRoles(newPage, apiPageInfo.pageSize);
    }
  };

  const handleShow = useCallback((role = null) => {
    if (role) {
      setFormData({
        id: role.id,
        roleName: role.roleName,
        description: role.description,
        isActive: role.isActive === 1 || role.isActive === true,
      });
    } else {
      setFormData({ id: 0, roleName: "", description: "", isActive: false });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => setShowModal(false), []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (formData.roleName && formData.description) {
        try {
          setLoading(true);
          // Ensure isActive is boolean
          const submitData = { ...formData, isActive: Boolean(formData.isActive) };
          await addOrUpdateRole(submitData);
          handleClose();
          dashboardToast.success("Role saved successfully");
          fetchRoles(apiPageInfo.currentPage, apiPageInfo.pageSize);
        } catch (err) {
          console.error("Error saving role:", err);
          dashboardToast.error("Failed to save role. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    },
    [formData, handleClose, fetchRoles, apiPageInfo.currentPage, apiPageInfo.pageSize]
  );

  const handleDelete = useCallback(
    (roleId) => {
      const role = roles.find((r) => r.id === roleId);
      setRoleToDelete(role);
      setShowDeleteModal(true);
    },
    [roles]
  );

  const confirmDelete = useCallback(async () => {
    try {
      setLoading(true);
      await deleteRole(roleToDelete.id);
      dashboardToast.success("Role and associated users deleted");
      fetchRoles(apiPageInfo.currentPage, apiPageInfo.pageSize);
    } catch (err) {
      console.error("Error deleting role:", err);
      dashboardToast.error(
        err?.message || "Failed to delete role. Please try again."
      );
    } finally {
      setShowDeleteModal(false);
      setRoleToDelete(null);
      setLoading(false);
    }
  }, [roleToDelete, fetchRoles, apiPageInfo.currentPage, apiPageInfo.pageSize]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
  }, []);

  const openViewModal = useCallback((role) => {
    setViewRecord(role);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
  }, []);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Roles">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Roles</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage roles used for access control across the platform.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
                Create Role
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading roles…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          <span className="app-td__desc">
                            No roles found. Create your first role.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      roles.map((role) => {
                        const displayName = role.roleName || "Untitled role";

                        return (
                          <tr
                            key={role.id}
                            className="rdm-row-clickable"
                            onClick={() => openViewModal(role)}
                          >
                            <td>
                              <span className="app-td__title">{displayName}</span>
                            </td>
                            <td>
                              {role.description ? (
                                <span className="app-td__desc" title={role.description}>
                                  {role.description}
                                </span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              <span
                                className={
                                  role.isActive
                                    ? "app-status app-status--success"
                                    : "app-status app-status--muted"
                                }
                              >
                                {role.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td
                              className="dash-mobile-table-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="btn-list-actions">
                                <UiButton
                                  variant="outline"
                                  size="sm"
                                  className="btn-list-edit"
                                  title="Edit"
                                  aria-label={`Edit ${displayName}`}
                                  onClick={() => handleShow(role)}
                                >
                                  <Pencil />
                                </UiButton>
                                <UiButton
                                  variant="danger"
                                  size="sm"
                                  className="btn-list-delete"
                                  title="Delete"
                                  aria-label={`Delete ${displayName}`}
                                  onClick={() => handleDelete(role.id)}
                                >
                                  <Trash2 />
                                </UiButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.currentPage}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          <Shield className="h-5 w-5 me-2" />
          <Modal.Title>
            {formData.id ? "Edit Role" : "Create New Role"}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4">
            <Row>
              <Col md={6}>
                <Form.Group controlId="formRoleName" className="mb-3">
                  <Form.Label className="required">Role Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="roleName"
                    placeholder="Enter role name"
                    value={formData.roleName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formDescription" className="mb-3">
                  <Form.Label className="required">Description</Form.Label>
                  <Form.Control
                    type="text"
                    name="description"
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="formIsActive" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active role?"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn btn-dashboard-cancel" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="btn btn-dashboard-primary"
              type="submit"
            >
              Save Role
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the role "{roleToDelete?.roleName}"?
          All users assigned to this role will also be permanently deleted. This
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={cancelDelete}
            style={{ width: "auto" }}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            style={{ width: "auto" }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRecord?.roleName || "Role Details"}
        subtitle="Role record"
        fields={[
          { label: "Role Name", value: viewRecord?.roleName },
          {
            label: "Description",
            value: viewRecord?.description,
            fullWidth: true,
          },
          { label: "Active", value: Boolean(viewRecord?.isActive) },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const role = viewRecord;
              closeViewModal();
              if (role) handleShow(role);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const role = viewRecord;
              closeViewModal();
              if (role) handleDelete(role.id);
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default Role;
